import {
  ColDef,
  ColDefField,
  DataTypeDefinition,
  ValueFormatterFunc,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { alignColumn, alignLeft, alignRight, comparator, component, mergeClasses } from './utils';
import { formatPrettyNumber } from '@/modules/common/components/pretty-number';
import { PRICE_PRECISION, RATE_PRECISION } from '@/modules/common/constants/precision';
import { getPriceAsString } from '@/utils/helpers/auction-numbers';
import Decimal from 'decimal.js';
import { SupportedDates, formatAnyDate } from '@/utils/helpers/dates';
import { Benchmark, benchmarkFromProtoEnum } from '@/utils/api/loans';
import { computed, h } from 'vue';
import FormatSide from '@/modules/common/components/format-side/FormatSide.vue';
import RateOutput from '@/modules/common/components/format-rate/RateOutput.vue';
import { ErrorItem, Security } from '@/modules/common/models';
import {
  Agreement,
  Data,
  Benchmark as PbBenchmark,
  PbRateDetails,
  SettlementType,
} from '@/connect';
import { filterBigInt, filterDate, filterDateTime, filterDecimal, filterTime } from './filter';
import PriceChangeOutput from '@/modules/common/components/format-price/PriceChangeOutput.vue';
import WrapWithEllipisAndTooltip from '@/modules/common/components/WrapWithEllipisAndTooltip.vue';
import AgreementPopover from '@/modules/common/components/AgreementPopover.vue';
import { formatUserIdentifier } from '@/modules/common/helpers/formatters';
import { VTooltip } from 'vuetify/lib';
import { getAgreementDetailsFromAgreement, useAgreementsStore } from '@/store/agreements-store';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const CustomDataType = {
  CUSIP: 'CUSIP',
  Timestamp: 'Timestamp',
  TimestampWithSeconds: 'TimestampWithSeconds',
  TimestampAsTime: 'TimestampAsTime',
  TimestampAsDate: 'TimestampAsDate',
  TimestampAsDateWithYear: 'TimestampAsDateWithYear',
} as const;

export type CustomDataType = (typeof CustomDataType)[keyof typeof CustomDataType];

export const dataTypeDefinitions = {
  date: {
    extendsDataType: 'date',
    baseDataType: 'date',
    valueFormatter: (params: ValueFormatterParams<unknown, SupportedDates | undefined>): string => {
      return formatAnyDate(params.value, 'MMM dd h:mm a');
    },
    useValueFormatterForExport: false,
  },
  [CustomDataType.CUSIP]: {
    extendsDataType: 'text',
    baseDataType: 'text',
    valueFormatter: (params: ValueFormatterParams<unknown, string>): string => params.value ?? '',
  },
  [CustomDataType.Timestamp]: {
    extendsDataType: 'date',
    baseDataType: 'date',
    valueFormatter: (params: ValueFormatterParams<unknown, SupportedDates | undefined>): string => {
      return formatAnyDate(params.value, 'MMM dd h:mm a');
    },
    useValueFormatterForExport: false,
  },
  [CustomDataType.TimestampWithSeconds]: {
    extendsDataType: CustomDataType.Timestamp,
    baseDataType: 'date',
    valueFormatter: (params: ValueFormatterParams<unknown, SupportedDates | undefined>): string => {
      return formatAnyDate(params.value, 'MMM dd h:mm:ss a');
    },
    useValueFormatterForExport: false,
  },
  [CustomDataType.TimestampAsDate]: {
    extendsDataType: CustomDataType.Timestamp,
    baseDataType: 'date',
    valueFormatter: (params: ValueFormatterParams<unknown, SupportedDates | undefined>): string => {
      return formatAnyDate(params.value, 'MMM dd');
    },
    useValueFormatterForExport: false,
  },
  [CustomDataType.TimestampAsTime]: {
    extendsDataType: CustomDataType.Timestamp,
    baseDataType: 'date',
    valueFormatter: (params: ValueFormatterParams<unknown, SupportedDates | undefined>): string => {
      return formatAnyDate(params.value, 'h:mm a');
    },
    useValueFormatterForExport: false,
  },
  [CustomDataType.TimestampAsDateWithYear]: {
    extendsDataType: CustomDataType.Timestamp,
    baseDataType: 'date',
    valueFormatter: (params: ValueFormatterParams<unknown, SupportedDates | undefined>): string => {
      return formatAnyDate(params.value, 'M/dd/yy');
    },
    useValueFormatterForExport: false,
  },
};

export function getValueFormatter(colDef: ColDef): ValueFormatterFunc | undefined {
  return colDef.valueFormatter ||
    (typeof colDef.cellDataType === 'string' && dataTypeDefinitions[colDef.cellDataType])
    ? (dataTypeDefinitions[colDef.cellDataType as CustomDataType] as DataTypeDefinition)
        .valueFormatter
    : undefined;
}

// default colDef.useValueFormatterForExport to TRUE
// this should be the default, but doesn't seem to actually be the case.
export function setDefaultUseValueFormatterForExport(colDef: ColDef): void {
  if (
    typeof colDef.useValueFormatterForExport === 'undefined' &&
    !(
      colDef.cellDataType &&
      typeof colDef.cellDataType === 'string' &&
      typeof dataTypeDefinitions[colDef.cellDataType]?.useValueFormatterForExport !== 'undefined'
    )
  ) {
    colDef.useValueFormatterForExport = true;
  }
}

export function prettyPrice(value: Decimal | number, precision = PRICE_PRECISION): string {
  return getPriceAsString(value, precision);
}

interface ColConfig<T> {
  field: ColDefField<T>;
  headerName: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  colId?: string;
  filter?: boolean;
  initialHide?: boolean;
}

export function checkbox(): ColDef<unknown> {
  return {
    colId: 'checkbox',
    headerCheckboxSelection: true,
    headerClass: 'header-checkbox',
    // it would be great to always set
    // headerCheckboxSelectionCurrentPageOnly: true
    // but, it's not supported with "serverSide" row model, so AgTableClient sets it
    // ---
    // note for the future upgrade to v33:
    // the whole API to select items changed significantly in V32 (https://www.ag-grid.com/javascript-data-grid/upgrading-to-ag-grid-32-2-1/)
    // it seems even when we upgrade to v32/v33 the issue will be the same: (https://github.com/ag-grid/ag-grid/issues/10688)
    checkboxSelection: true,
    showDisabledCheckboxes: true,
    resizable: false,
    pinned: 'left',
    lockVisible: true,
    lockPosition: true,
    sortable: false,
    width: 36,
    maxWidth: 36,
    suppressSizeToFit: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    suppressColumnsToolPanel: true,
  };
}

export function rowDrag(): ColDef<unknown> {
  return {
    colId: 'rowDrag',
    rowDrag: true,
    resizable: false,
    pinned: 'left',
    lockVisible: true,
    lockPosition: true,
    sortable: false,
    width: 36,
    maxWidth: 36,
    suppressSizeToFit: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    suppressColumnsToolPanel: true,
    cellClass: 'row-drag-cell',
  };
}

// format number with thousands separator and 2 decimal places aligning right
export function quantity<T>({
  field,
  headerName,
  align = 'right',
  colId = undefined,
  initialHide = false,
  dashWhenZero = false,
  filter = false,
  sortable = true,
}: ColConfig<T> & { dashWhenZero?: boolean; initialHide?: boolean }): ColDef<T> {
  return {
    field,
    colId: colId ?? field,
    headerName,
    initialHide,
    valueFormatter: (params: ValueFormatterParams<T, bigint>) => {
      if (dashWhenZero && !params.value) {
        return '–';
      }

      return formatPrettyNumber(params.value ?? 0n);
    },
    useValueFormatterForExport: false,
    comparator: comparator.bigint,
    // for very specific cases, we want to align left (like top/depth of book views that mirror sides)
    ...alignColumn({ align }),
    ...(filter && filterBigInt({ allowNegative: false })),
    sortable,
  };
}

export function price<T>({
  field,
  headerName,
  align = 'right',
  filter,
  initialHide = false,
}: ColConfig<T>): ColDef<T, Decimal | string | number> {
  return {
    field,
    colId: field,
    headerName,
    initialHide,
    valueFormatter: (params: ValueFormatterParams) => `$${prettyPrice(params.value)}`,
    useValueFormatterForExport: false,
    comparator: comparator.decimal,
    ...alignColumn({ align }),
    ...(filter && filterDecimal({ allowNegative: false })),
  };
}

export function priceWithPinnedAgg<T>({
  field,
  headerName,
  pinnedValueGetter,
  align = 'right',
  initialHide = false,
}: ColConfig<T> & {
  pinnedValueGetter: (params: ValueFormatterParams<T, Decimal | number> & { data: T }) => Decimal;
}): ColDef<T, Decimal | number> {
  return {
    field,
    colId: field,
    headerName,
    initialHide,
    valueFormatter: (params: ValueFormatterParams<T, Decimal | number>) => {
      if (params.node?.rowPinned && params.data) {
        return `$${prettyPrice(pinnedValueGetter(params as ValueFormatterParams<T, Decimal | number> & { data: T }))}`;
      }
      if (!params.value) {
        return `$${prettyPrice(0)}`;
      }
      return `$${prettyPrice(params.value)}`;
    },
    useValueFormatterForExport: false,
    comparator: comparator.decimal,
    ...alignColumn({ align }),
  };
}

export const timestampAdapter = component<{ date: string; time?: string }>(
  (props) => () =>
    h(
      'div',
      props.time === '–'
        ? [h('span', props.date)]
        : [h('span', props.date), h('span', { class: 'ml-1 font-weight-light' }, props.time ?? '')]
    )
);

export function timestamp<T>({
  field,
  headerName,
  align = 'left',
  filter,
}: Omit<ColConfig<T>, 'filter'> & { filter?: 'date' | 'datetime' | 'time' | false }): ColDef<T> {
  return {
    field,
    colId: field,
    headerName,
    cellDataType: CustomDataType.Timestamp,
    cellRendererSelector: (params) => {
      const normalizedValue = params.value;
      return timestampAdapter({
        date: formatAnyDate(normalizedValue, 'MMM dd'),
        time: formatAnyDate(normalizedValue, 'hh:mm a'),
      });
    },
    comparator: comparator.date,
    initialWidth: 150,
    minWidth: 190,
    ...alignColumn({ align }),
    ...(filter === 'datetime' && filterDateTime()),
    ...(filter === 'date' && filterDate()),
    ...(filter === 'time' && filterTime()),
  };
}

export function date<T>({ field, headerName, align = 'left', filter }: ColConfig<T>): ColDef<T> {
  return {
    field,
    colId: field,
    headerName,
    cellDataType: CustomDataType.TimestampAsDate,
    comparator: comparator.date,
    ...alignColumn({ align }),
    ...(filter && filterDate()),
  };
}

export function time<T>({ field, headerName, align = 'left', filter }: ColConfig<T>): ColDef<T> {
  return {
    field,
    colId: field,
    headerName,
    cellDataType: CustomDataType.TimestampAsTime,
    comparator: comparator.timestamp,
    ...alignColumn({ align }),
    ...(filter && filterTime()),
  };
}

export function cusip<T extends { security: Security }>(): ColDef<T, string> {
  return {
    field: 'security.cusip' as ColDefField<T>,
    colId: 'security.cusip',
    headerName: 'CUSIP',
    cellDataType: CustomDataType.CUSIP,
    ...alignLeft(),
  };
}

export const priceChangeAdapter = component<{
  lastClosePrice: Decimal;
  lastPrice: Decimal;
}>((props) => () => h(PriceChangeOutput, { props }));

export const rateAdapter = component<{
  rate: null | undefined | number | string | Decimal;
  rateModifier: undefined | Benchmark;
  precision?: number;
  omitPercentageSign?: boolean;
}>(
  (props) => () =>
    props.rate === null || props.rate === undefined
      ? h('span', undefined, '–')
      : h(RateOutput, { props })
);

export function rate<
  T extends { rate: Decimal; rateModifier: undefined | Benchmark } | { rate?: string } | undefined,
>(): ColDef<T> {
  return {
    field: 'rate' as ColDefField<T>,
    colId: 'rate',
    headerName: 'Rebate Rate',
    cellRendererSelector: (params) =>
      rateAdapter({
        rate: params.value ?? null,
        rateModifier:
          params.data && 'rateModifier' in params.data ? params.data.rateModifier : undefined,
        precision: undefined,
      }),
    comparator: comparator.decimal,
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
  };
}

export const formatSideAdapter = component<{ side: 'BORROW' | 'LOAN' }>(
  (props) => () => h(FormatSide, { props })
);

export function side<T extends { side: 'BORROW' | 'LOAN' }>(): ColDef<T> {
  return {
    field: 'side' as ColDefField<T>,
    colId: 'side',
    headerName: 'Side',
    cellRendererSelector: (params) =>
      formatSideAdapter({
        side: params.value,
      }),
    ...alignLeft(),
  };
}

export function ticker<T extends { security: Security }>(): ColDef<T, string> {
  return {
    field: 'security.ticker' as ColDefField<T>,
    colId: 'security.ticker',
    headerName: 'Ticker',
    ...alignLeft(),
  };
}

export function securityType<T extends { security: Security }>(): ColDef<T, string> {
  return {
    field: 'security.securityType' as ColDefField<T>,
    colId: 'security.securityType',
    headerName: 'Sec Type',
    ...alignLeft(),
  };
}

const settlementOptions: Array<{ value: string; text: string }> = [
  { value: 'NSCC', text: 'NSCC' },
  { value: 'BILATERAL', text: 'Bilateral' },
  { value: 'OCC', text: 'OCC' },
];

export function settlementTypeStringLabel(settlType: string): string {
  const item = settlementOptions.find((i) => i.value === settlType);
  return item ? item.text : '–';
}

export function settlementTypeString<T extends { settlementType: string }>(): ColDef<T> {
  return {
    field: 'settlementType' as ColDefField<T>,
    colId: 'settlementType',
    headerName: 'Sett Type',
    initialHide: true,
    ...alignLeft(),
    valueFormatter: (params) => settlementTypeStringLabel(params.value),
    minWidth: 80,
  };
}

export function settlementTypeEnum<T extends { settlementType: SettlementType }>(): ColDef<T> {
  return {
    field: 'settlementType' as ColDefField<T>,
    colId: 'settlementType',
    headerName: 'Sett Type',
    valueFormatter: (params) => settlementTypeStringLabel(SettlementType[params.value]),
    ...alignLeft(),
  };
}

export function formatPbRateDetails(
  details: Data<PbRateDetails>,
  precision = RATE_PRECISION
): string {
  const r = new Decimal(details.rate);

  if (details.benchmark === undefined || details.benchmark === PbBenchmark.UNSPECIFIED) {
    return `${r.toFixed(precision)}`;
  }

  const sign = Math.sign(r.toNumber()) === -1 ? '-' : '+';

  return `${benchmarkFromProtoEnum(details.benchmark)} ${sign} ${r.abs().toFixed(precision)}`;
}

export const agreementPopoverAdapter = component<{
  agreementIds?: string[];
  emptyValue?: string;
}>((props) => () => h(AgreementPopover, { props }));

export const agreementAdapter = component<{
  agreementIds?: string[];
  emptyValue?: string;
}>((props) => {
  const agreementsStore = useAgreementsStore();

  const agreements = computed(() => {
    return (props.agreementIds ?? [])
      .map((agreementId) => agreementsStore.getAgreement(agreementId))
      .filter((agreement): agreement is Agreement => !!agreement);
  });

  return () =>
    agreements.value.length > 0
      ? h(
          'div',
          { style: { display: 'contents' } },
          agreements.value.map((agreement, agreementIndex) => {
            return h('span', undefined, [
              agreementIndex !== 0 ? h('span', undefined, ', ') : undefined,
              h(
                VTooltip,
                {
                  props: { bottom: true },
                  scopedSlots: {
                    activator: ({ on, attrs }) =>
                      h('span', { props: attrs, on, style: 'display: inline-flex;' }, [
                        agreement.shortName,
                      ]),
                  },
                },
                [getAgreementDetailsFromAgreement(agreement)]
              ),
            ]);
          })
        )
      : h('span', undefined, props.emptyValue ?? 'Any');
});

export const orderTypeAdapter = component<{ orderType: string }>(
  (props) => () => h('span', { class: 'text-capitalize' }, props.orderType)
);

export const orderIntentAdapter = component<{ orderIntent: string }>(
  (props) => () => h('span', { class: 'text-capitalize' }, props.orderIntent)
);

// , string | CellRendererSelectorResult | undefined
export function userIdentifier<T>(): Partial<ColDef<T>> {
  return {
    cellDataType: 'text',
    valueFormatter: (params) => {
      if (params.node?.rowPinned) {
        return '';
      }

      return params.value ? formatUserIdentifier(params.value) : '–';
    },
    cellRendererSelector: (params) => {
      return params.node.rowPinned
        ? undefined
        : wrapWithEllipsisAndTooltipAdapter({
            value: formatUserIdentifier(params.value ?? '–'),
          });
    },
    ...alignLeft(),
  };
}

export function errorRow(): ColDef<ErrorItem, number> {
  return {
    field: 'row',
    colId: 'row',
    headerName: 'Row',
    sortable: false,
    cellDataType: 'text',
    ...mergeClasses([alignLeft(), { cellClass: 'warning--text' }]),
  };
}

export function errorField(): ColDef<ErrorItem, string> {
  return {
    field: 'field',
    colId: 'field',
    headerName: 'Field',
    sortable: false,
    cellDataType: 'text',
    ...alignLeft(),
    ...mergeClasses([alignLeft(), { cellClass: 'warning--text' }]),
  };
}

export function errorMessage(config?: {
  classes: Pick<ColDef<ErrorItem, string>, 'headerClass' | 'cellClass'>;
}): ColDef<ErrorItem, string> {
  return {
    field: 'errMsg',
    colId: 'errMsg',
    headerName: 'Error',
    sortable: false,
    cellDataType: 'text',
    ...alignLeft(),
    ...mergeClasses([
      alignLeft(),
      ...(config ? [config.classes] : []),
      { cellClass: 'warning--text' },
    ]),
  };
}
export const wrapWithEllipsisAndTooltipAdapter = component<{
  value: string;
}>((props) => () => h(WrapWithEllipisAndTooltip, { props }));
